import { useDesign } from "../lib/store";
import { useAuth } from "../lib/auth";
import { useCollaborators } from "../lib/useCollaborators";
import { initialsFromName } from "../lib/cursorColor";

/**
 * Avatar stack rendered in the TopBar showing other collaborators
 * currently active in the shared room. Renders nothing for private
 * sessions (i.e. when `activeSharedRoomId` is null) or when no one else
 * is currently connected.
 */
export function PresenceAvatars() {
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const auth = useAuth();
  const collaborators = useCollaborators(sharedRoomId, auth.user?.uid ?? null);

  const others = Array.from(collaborators.values());
  if (others.length === 0) return null;
  if (sharedRoomId && !auth.user) return null;

  return (
    <div className="flex items-center -space-x-1.5 mr-1" data-testid="presence-avatars">
      {others.slice(0, 3).map((c) => (
        <Avatar
          key={c.uid}
          title={c.displayName ?? "Collaborator"}
          photoURL={c.photoURL}
          displayName={c.displayName}
          color={c.color}
        />
      ))}
      {others.length > 3 && (
        <div
          title={`${others.length - 3} more`}
          className="size-7 rounded-full ring-2 ring-ink-50/85 bg-ink-200 text-ink-700 flex items-center justify-center text-label font-semibold"
        >
          +{others.length - 3}
        </div>
      )}
    </div>
  );
}

function Avatar({
  title,
  photoURL,
  displayName,
  color,
}: {
  title: string;
  photoURL: string | null;
  displayName: string | null;
  color: string;
}) {
  if (photoURL) {
    return (
      <div
        title={title}
        className="size-7 rounded-full ring-2 ring-ink-50/85 overflow-hidden"
        style={{ outline: `2px solid ${color}`, outlineOffset: "-2px" }}
      >
        <img
          src={photoURL}
          alt=""
          referrerPolicy="no-referrer"
          className="size-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      title={title}
      className="size-7 rounded-full ring-2 ring-ink-50/85 text-white flex items-center justify-center text-caption font-semibold"
      style={{ backgroundColor: color }}
    >
      {initialsFromName(displayName)}
    </div>
  );
}
